/**
 * React binding for the WS client.
 *
 * Responsibilities (see wp7-client-ui-input.md § "WebSocket client"):
 *   • Own the ONE `PitSocket` instance for the tab.
 *   • Own the ONE `WsSnapshotSource` (WP6 subscribes to it).
 *   • Route parsed server messages into the store reducer + snapshot
 *     source, keeping snapshots OUT of React state.
 *   • Idempotent connect (StrictMode double-mount safe).
 *   • Provide `useWsClient()` to descendants for send helpers.
 *
 * The context value never changes across renders (useRef); consumers
 * that need re-render on status must read `useStore()`.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactElement,
  type ReactNode,
} from "react";
import type {
  MatchId,
  ChampionId,
  Loadout,
  OpponentSeat,
  QueueSpec,
  ServerMessage,
  SimInput,
} from "@pit/protocol";
import { PitSocket, browserWsFactory, type WsFactory } from "./socket.js";
import { WsSnapshotSource, handleSnapshotMessage } from "./snapshotSource.js";
import { useStore } from "../store/context.js";
import { resolveWsUrlFromWindow } from "./resolveWsUrl.js";
import { pushTestHookRefs } from "../test-hook.js";

export interface WsClient {
  /** Send a typed sim input; returns assigned seq or null if unsent. */
  readonly sendInput: (input: SimInput) => void;
  /** Create a new match (returns to server via `matchCreated`). `loadout`
   *  is the chosen ability subset (#323); `null` = the champion's full pool.
   *  `opts.teammateSeats`/`opts.opponentSeats` (#676) and `opts.mapSlug`
   *  (#678) are practice-mode-only — omit for an ordinary create. */
  readonly createMatch: (
    championId: ChampionId,
    loadout: Loadout,
    opts?: {
      readonly teammateSeats?: readonly OpponentSeat[];
      readonly opponentSeats?: readonly OpponentSeat[];
      readonly practiceBotCount?: number;
      readonly mapSlug?: string;
    },
  ) => void;
  /** Join a match by id. */
  readonly joinMatch: (id: MatchId, championId: ChampionId, loadout: Loadout) => void;
  /** Quick-join a matchmaking queue (create-or-join an open room). */
  readonly quickJoin: (queue: QueueSpec, championId: ChampionId, loadout: Loadout) => void;
  /** Leave the room this connection is currently attached to (#332). */
  readonly leaveMatch: () => void;
  /** Request the promoted bot roster for the practice-mode setup screen
   *  (#499) — reply arrives as a `botList` message, routed to the store. */
  readonly listBots: () => void;
  /** Send an in-game chat message (#833). Trimmed + length-capped by the
   *  socket layer; blank text sends nothing. No ack — and, since #855, no
   *  guarantee of delivery either (the server drops slurs and rate-limited
   *  messages silently), which is why this returns the exact text that went
   *  on the wire (or `null`): the sender's own echo is rendered LOCALLY from
   *  it, never from the round trip. See `ChatInput`. */
  readonly sendChat: (text: string) => string | null;
  /** WP6-facing SnapshotSource. */
  readonly snapshotSource: WsSnapshotSource;
}

const WsCtx = createContext<WsClient | null>(null);

export interface WsClientProviderProps {
  readonly children: ReactNode;
  /** Override URL — tests inject a stub; production reads window. */
  readonly url?: string;
  /** Override factory — tests inject a fake WebSocket. */
  readonly factory?: WsFactory;
  /** Disable auto-connect — for isolated component tests / demo harness. */
  readonly autoConnect?: boolean;
}

export function WsClientProvider({
  children,
  url,
  factory,
  autoConnect,
}: WsClientProviderProps): ReactElement {
  const { state, dispatch } = useStore();
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const source = useMemo(() => new WsSnapshotSource(), []);
  const socket = useMemo(() => {
    return new PitSocket({
      url: url ?? resolveWsUrlFromWindow(),
      factory: factory ?? browserWsFactory,
    });
    // Only construct once per mount; url/factory changes are not supported
    // (React would need a full remount to re-init the socket safely).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    socket.setListeners({
      onStatus: (status) => {
        dispatchRef.current({
          type: "connection.status",
          next: {
            kind: status.kind,
            sessionId: status.kind === "welcomed" ? status.sessionId : null,
            serverVersion: status.kind === "protocolMismatch" ? status.serverVersion : null,
            cleanClose: status.kind === "closed" ? status.clean : false,
            testerFlagged: status.kind === "welcomed" ? status.testerFlagged : false,
          },
        });
      },
      onMessage: (msg: ServerMessage) => {
        // Snapshots: OUT of React state — direct to the source.
        if (handleSnapshotMessage(source, msg)) return;
        // Route non-snapshot messages through the reducer.
        dispatchServerMessage(msg, dispatchRef.current);
      },
    });
    if (autoConnect !== false) {
      socket.connect();
    }
    return () => {
      socket.dispose();
    };
  }, [socket, source, autoConnect]);

  const client = useMemo<WsClient>(
    () => ({
      sendInput: (input) => {
        socket.sendInput(input);
      },
      createMatch: (championId, loadout, opts) => {
        socket.createMatch(championId, loadout, opts);
      },
      joinMatch: (id, championId, loadout) => {
        socket.joinMatch(id, championId, loadout);
      },
      quickJoin: (queue, championId, loadout) => {
        socket.quickJoin(queue, championId, loadout);
      },
      leaveMatch: () => {
        socket.leaveMatch();
      },
      listBots: () => {
        socket.listBots();
      },
      sendChat: (text) => socket.sendChat(text),
      snapshotSource: source,
    }),
    [socket, source],
  );

  // WP8: publish live refs to the test hook (no-op unless ?pitTest=1).
  pushTestHookRefs(state, client);

  return <WsCtx.Provider value={client}>{children}</WsCtx.Provider>;
}

export function useWsClient(): WsClient {
  const v = useContext(WsCtx);
  if (v === null) {
    throw new Error("useWsClient: must be used within <WsClientProvider>");
  }
  return v;
}

/**
 * Server message → store dispatch mapping. Kept as a pure function so
 * message-handling can be unit-tested independently of the React
 * plumbing.
 */
export function dispatchServerMessage(
  msg: ServerMessage,
  dispatch: (a: import("../store/types.js").Action) => void,
): void {
  switch (msg.type) {
    case "welcome":
    case "protocolMismatch":
    case "opponentConnection":
      // Life-cycle already tracked in `onStatus` (welcome / protocolMismatch);
      // `opponentConnection` is a UX hint not yet wired at MVP.
      return;
    case "error":
      // Previously swallowed ("not yet wired at MVP"), which made a rejected
      // create/join/quickJoin a silent dead-end (see #22). Surface it so the
      // UI can reset any pending action and the user isn't stuck.
      dispatch({ type: "serverError", code: msg.code, ts: Date.now() });
      return;
    case "matchCreated":
      dispatch({
        type: "match.created",
        matchId: msg.matchId,
        joinUrl: msg.joinUrl,
        mapSlug: msg.mapSlug,
      });
      // The server now sends the authoritative assigned seat (creator or
      // quick-join joiner may be seat 0 or 1). Use it directly instead of
      // guessing, so the follow-camera targets the right champion.
      dispatch({
        type: "match.joined",
        matchId: msg.matchId,
        ownSeat: msg.seat,
      });
      return;
    case "matchPhase":
      dispatch({
        type: "match.phase",
        phase: msg.phase,
        winner: msg.winner,
        roster: msg.roster,
        endsAtTick: msg.endsAtTick,
        damageDealt: msg.damageDealt,
        mapSlug: msg.mapSlug,
      });
      return;
    case "snapshot":
      // Should have been intercepted upstream. Silent drop — snapshots
      // are load-bearing and go through the SnapshotSource, not the store.
      return;
    case "inputRejected":
      dispatch({
        type: "rejection.add",
        reason: msg.reason,
        ts: Date.now(),
      });
      return;
    case "botList":
      dispatch({ type: "practice.botList", bots: msg.bots });
      return;
    case "matchJoined":
      // #742: the `joinMatch` (share-link) path's own seat ack — the
      // `matchCreated` analogue above, for the OTHER way a client enters a
      // match. Replaces `JoinScreen`'s old hardcoded `ownSeat: 1` guess
      // (right for 1v1, wrong for a 3v3 practice joiner landing in any of
      // seats 1-5) with the server's real, join-order-assigned seat.
      dispatch({
        type: "match.joined",
        matchId: msg.matchId,
        ownSeat: msg.seat,
      });
      return;
  }
}
